import { ReactNode } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import InfiniteScroll from 'react-infinite-scroller';
import {
  InfiniteScrollEndMessage,
  InfiniteScrollErrorMessage,
  InfiniteScrollLoading,
} from '@components/InfiniteScroll';

interface NoticeboardSwimlaneProps<T> {
  title: string;
  name: string;
  itemCount: number;
  items: T[];
  hasMoreData: boolean | null;
  isLoadingError: boolean | null;
  loadMore: () => void;
  renderItem: (item: T) => ReactNode;
  handleScrollToTop: () => void;
  onAddClick?: () => void;
  showAddButton?: boolean;
  addButtonDisabled?: boolean;
  headerContent?: ReactNode;
}

export function NoticeboardSwimlane<T extends { id: string }>({
  title,
  name,
  itemCount,
  items,
  hasMoreData,
  isLoadingError,
  loadMore,
  renderItem,
  handleScrollToTop,
  onAddClick,
  showAddButton = false,
  addButtonDisabled = false,
  headerContent,
}: NoticeboardSwimlaneProps<T>) {
  const scrollParentId = `${name}-list`;

  return (
    <div className='flex flex-col bg-gray-100 rounded-lg min-w-[260px] w-[82vw] max-w-[300px] sm:min-w-[280px] sm:w-[280px] sm:max-w-none h-full border border-gray-200'>
      {/* Header */}
      <div className='flex items-center justify-between px-3 py-2.5 bg-white rounded-t-lg border-b border-gray-200'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-semibold text-gray-900'>{title}</h3>
          <span className='inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded'>
            {itemCount}
          </span>
        </div>
        <div className='flex items-center gap-0.5'>
          {headerContent}
          {showAddButton && (
            <button
              type='button'
              onClick={onAddClick}
              disabled={addButtonDisabled}
              className='p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              title='Add'
            >
              <PlusIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      </div>

      {/* Cards Container */}
      <div id={scrollParentId} className='flex-1 overflow-y-auto p-2 space-y-2'>
        <InfiniteScroll
          initialLoad
          loadMore={loadMore}
          hasMore={
            hasMoreData === null || (hasMoreData === true && !isLoadingError)
          }
          useWindow={false}
          getScrollParent={() => document.getElementById(scrollParentId)}
          loader={
            <InfiniteScrollLoading
              key={`${name}-loader`}
              loadingMessage={`Loading ${name}...`}
            />
          }
        >
          <div className='space-y-2'>
            {items.length > 0 && items.map((item) => renderItem(item))}
          </div>
          {items.length === 0 && !hasMoreData && (
            <div className='text-center py-8 text-gray-500 text-sm'>
              No {name} items
            </div>
          )}
          {hasMoreData === false && items.length > 0 && (
            <InfiniteScrollEndMessage
              isVisible={items.length > 25}
              direction='column'
              endMessageText={`End of ${name}`}
              handleScrollToTop={handleScrollToTop}
            />
          )}
          {isLoadingError && (
            <InfiniteScrollErrorMessage
              errorMessage={`Error loading ${name}`}
            />
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
}
